import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, X, ArrowLeft, UserCheck, Clock } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Spinner, EmptyState } from "@/components/common";

function StatusBadge({ status }) {
  const map = {
    pending: "bg-amber-50 text-amber-700 border-amber-200",
    accepted: "bg-emerald-50 text-emerald-700 border-emerald-200",
    declined: "bg-slate-100 text-slate-500 border-slate-200",
  };
  return <span className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold capitalize ${map[status]}`}>{status}</span>;
}

export default function Connections() {
  const [data, setData] = useState(null);

  const load = () => api.get("/connections").then(({ data }) => setData(data));
  useEffect(() => { load(); }, []);

  const respond = async (id, status) => {
    try {
      await api.put(`/connections/${id}`, { status });
      toast.success(`Request ${status}.`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  if (!data) return <div className="container-nx section-nx"><Spinner /></div>;

  return (
    <div className="container-nx section-nx max-w-3xl">
      <Link to="/dashboard" className="mb-6 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#0B192C]">
        <ArrowLeft className="h-4 w-4" /> Back to dashboard
      </Link>
      <h1 className="heading-2">My connections</h1>

      <div className="mt-8">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-[#0B192C] font-head"><UserCheck className="h-5 w-5 text-[#B8860B]" />Received requests</h2>
        <div className="mt-4 space-y-3">
          {data.incoming.length === 0 ? (
            <EmptyState title="No requests received" message="When members request to connect with you, they'll appear here." icon={UserCheck} />
          ) : (
            data.incoming.map((r) => (
              <div key={r.id} className="card-nx flex flex-col gap-3 p-5 sm:flex-row sm:items-center sm:justify-between" data-testid={`incoming-${r.id}`}>
                <div>
                  <Link to={`/members/${r.from_user_id}`} className="font-semibold text-[#0B192C] hover:underline">{r.from_user_name}</Link>
                  {r.message && <p className="mt-1 text-sm text-slate-500">"{r.message}"</p>}
                </div>
                {r.status === "pending" ? (
                  <div className="flex gap-2">
                    <button onClick={() => respond(r.id, "accepted")} className="btn-gold px-4 py-2 text-xs" data-testid={`accept-${r.id}`}><Check className="h-4 w-4" /> Accept</button>
                    <button onClick={() => respond(r.id, "declined")} className="btn-outline-navy px-4 py-2 text-xs" data-testid={`decline-${r.id}`}><X className="h-4 w-4" /> Decline</button>
                  </div>
                ) : <StatusBadge status={r.status} />}
              </div>
            ))
          )}
        </div>
      </div>

      <div className="mt-10">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-[#0B192C] font-head"><Clock className="h-5 w-5 text-[#B8860B]" />Sent requests</h2>
        <div className="mt-4 space-y-3">
          {data.outgoing.length === 0 ? (
            <EmptyState title="No requests sent" message="Browse the member directory to connect with others." icon={Clock} />
          ) : (
            data.outgoing.map((r) => (
              <div key={r.id} className="card-nx flex items-center justify-between p-5" data-testid={`outgoing-${r.id}`}>
                <Link to={`/members/${r.to_user_id}`} className="font-semibold text-[#0B192C] hover:underline">{r.to_user_name}</Link>
                <StatusBadge status={r.status} />
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
